import React, { useState } from 'react';
import { Phone, Mail, MessageCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PHONE_DISPLAY = '+91 98332 34958';
const PHONE_E164 = '919833234958';
const EMAIL = 'varad.anvekar@gmail.com';
const PROJECT_TYPES = ['Residential', 'Commercial', 'Hospitality', 'Retail', 'Other'];

const EMPTY = { name: '', email: '', phone: '', project_type: '', configuration: '', message: '' };

const buildWhatsAppText = (f) =>
  [
    `Hi Varad, I'd like to enquire about a photography shoot.`,
    ``,
    `Name: ${f.name}`,
    `Email: ${f.email}`,
    `Phone: ${f.phone}`,
    `Project: ${f.project_type}`,
    f.configuration ? `Configuration / Area: ${f.configuration}` : null,
    ``,
    f.message,
  ]
    .filter((l) => l !== null)
    .join('\n');

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="tool-label">{label}</span>
    {children}
  </label>
);

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    const waUrl = `https://wa.me/${PHONE_E164}?text=${encodeURIComponent(buildWhatsAppText(form))}`;
    const waWindow = window.open('', '_blank');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.[0]?.msg || data.detail || 'Something went wrong');
      if (waWindow) waWindow.location.href = waUrl;
      setStatus('sent');
      toast.success(data.email_sent ? 'Enquiry sent. Opening WhatsApp…' : 'Enquiry saved. Opening WhatsApp…');
      setForm(EMPTY);
    } catch (err) {
      if (waWindow) waWindow.close();
      setStatus('idle');
      toast.error(err.message);
    }
  };

  return (
    <div className="px-6 md:px-12 py-12 max-w-5xl" data-testid="contact-page">
      <h1 className="detail-header text-xl md:text-2xl mb-3">Contact</h1>
      <p className="text-sm text-gray-500 mb-12 max-w-lg">
        Tell us about your project and we'll get back within a day. Or reach us directly below.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-12 md:gap-20">
        <form onSubmit={submit} className="space-y-6" data-testid="contact-form" noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Name" testId="contact-name-field">
              <input className="tool-input" value={form.name} onChange={set('name')} required minLength={2} placeholder="Your name" data-testid="contact-name-input" />
            </Field>
            <Field label="Email" testId="contact-email-field">
              <input className="tool-input" type="email" value={form.email} onChange={set('email')} required placeholder="you@example.com" data-testid="contact-email-input" />
            </Field>
            <Field label="Phone" testId="contact-phone-field">
              <input className="tool-input" type="tel" value={form.phone} onChange={set('phone')} required placeholder="+91 ..." data-testid="contact-phone-input" />
            </Field>
            <Field label="Project type" testId="contact-type-field">
              <select className="tool-input" value={form.project_type} onChange={set('project_type')} required data-testid="contact-type-select">
                <option value="" disabled>Select</option>
                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Configuration / Area" testId="contact-config-field">
            <input className="tool-input" value={form.configuration} onChange={set('configuration')} placeholder="e.g. 3BHK, 1800 sq ft · Bandra" data-testid="contact-config-input" />
          </Field>
          <Field label="Message" testId="contact-message-field">
            <textarea className="tool-input min-h-[140px] resize-y" value={form.message} onChange={set('message')} required minLength={5} placeholder="Shoot date, location, deliverables…" data-testid="contact-message-input" />
          </Field>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
            <button type="submit" className="tool-btn flex items-center gap-2" disabled={status === 'sending'} data-testid="contact-submit-btn">
              {status === 'sent' ? <Check size={14} /> : <MessageCircle size={14} />}
              {status === 'sending' ? 'Sending…' : status === 'sent' ? 'Sent' : 'Send & open WhatsApp'}
            </button>
            <span className="text-xs text-gray-400">We'll receive your enquiry by email and WhatsApp.</span>
          </div>
        </form>

        <aside className="space-y-8 md:pt-1" data-testid="contact-direct">
          <div>
            <span className="tool-label">Call</span>
            <a href={`tel:+${PHONE_E164}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-black transition-colors" data-testid="contact-phone-link">
              <Phone size={14} strokeWidth={1.5} /> {PHONE_DISPLAY}
            </a>
          </div>
          <div>
            <span className="tool-label">WhatsApp</span>
            <a href={`https://wa.me/${PHONE_E164}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-700 hover:text-black transition-colors" data-testid="contact-whatsapp-link">
              <MessageCircle size={14} strokeWidth={1.5} /> Chat on WhatsApp
            </a>
          </div>
          <div>
            <span className="tool-label">Email</span>
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-black transition-colors break-all" data-testid="contact-email-link">
              <Mail size={14} strokeWidth={1.5} /> {EMAIL}
            </a>
          </div>
          <div>
            <span className="tool-label">Studio</span>
            <p className="text-sm text-gray-700">Mumbai, India</p>
          </div>
          <div className="pt-6 border-t border-gray-100">
            <span className="tool-label">Social</span>
            <div className="flex flex-col gap-2">
              <a href="https://www.instagram.com/studiovaradanvekar/" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-black transition-colors" data-testid="contact-instagram">Instagram</a>
              <a href="https://www.linkedin.com/in/studiovaradanvekar/" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-black transition-colors" data-testid="contact-linkedin">LinkedIn</a>
              <a href="https://www.behance.net/chaitananvekar" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-black transition-colors" data-testid="contact-behance">Behance</a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Contact;
